import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, useToast } from './Toast';
import { formatInTimeZone } from 'date-fns-tz';
import {
  addContraction,
  getContractions,
  getHourlyStats,
  deleteContraction,
  deleteAllContractions,
} from '../services/contractionService';

const IST_TIMEZONE = 'Asia/Kolkata';

// ─── helpers ────────────────────────────────────────────────────────────────

/** Compute hourly stats locally from a contractions array (offline fallback) */
const computeStatsFromRecords = (records) => {
  if (!records.length) return [];
  const buckets = {};
  records.forEach((c) => {
    const hour = parseInt(formatInTimeZone(c.endTime, IST_TIMEZONE, 'HH'), 10);
    if (!buckets[hour]) buckets[hour] = { total: 0, count: 0 };
    buckets[hour].total += c.durationSec;
    buckets[hour].count += 1;
  });
  return Object.entries(buckets)
    .map(([h, { total, count }]) => ({
      hour: parseInt(h, 10),
      avg: Math.round(total / count),
      count,
    }))
    .sort((a, b) => a.hour - b.hour);
};

const fmtDateTime = (date) =>
  formatInTimeZone(date, IST_TIMEZONE, 'dd MMM yyyy, HH:mm:ss');

const fmtDuration = (seconds) => {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

const fmtElapsed = (seconds) => {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
};

const fmtHour = (h) => {
  const prev = h === 0 ? 23 : h - 1;
  const fmt = (n) => {
    if (n === 0) return '12 AM';
    if (n < 12) return `${n} AM`;
    if (n === 12) return '12 PM';
    return `${n - 12} PM`;
  };
  return `${fmt(prev)} - ${fmt(h)}`;
};

// ─── component ──────────────────────────────────────────────────────────────

const ContractionTracker = () => {
  const { currentUser, isSpecialUser } = useAuth();
  const navigate = useNavigate();
  const { toasts, showToast, removeToast } = useToast();

  // Timer state
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState(null); // Date object
  const [elapsed, setElapsed] = useState(0); // seconds

  // Records
  const [contractions, setContractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingContraction, setSavingContraction] = useState(false);
  // Hourly stats (pre-aggregated from contractionsStats collection)
  const [hourlyStats, setHourlyStats] = useState([]);
  const [showStats, setShowStats] = useState(false);

  const intervalRef = useRef(null);

  // ── access guard ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isSpecialUser) {
      showToast('You do not have permission to access this page', 'error');
      navigate('/');
    }
  }, [isSpecialUser, navigate]);

  // ── load from Firestore ───────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser || !isSpecialUser) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        // Use allSettled so an offline stats failure doesn't block loading records
        const [recordsResult, statsResult] = await Promise.allSettled([
          getContractions(currentUser.email),
          getHourlyStats(currentUser.email),
        ]);

        const records = recordsResult.status === 'fulfilled' ? recordsResult.value : [];
        if (recordsResult.status === 'rejected') {
          console.error('Error loading records:', recordsResult.reason);
          showToast('Failed to load contraction records', 'error');
        }
        setContractions(records);

        if (statsResult.status === 'fulfilled') {
          setHourlyStats(statsResult.value);
        } else {
          // Offline fallback: compute from the records we already have
          console.warn('Stats fetch failed, computing locally:', statsResult.reason.message);
          setHourlyStats(computeStatsFromRecords(records));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, isSpecialUser]);

  // ── ticker ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isRunning) {
      intervalRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning]);

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleStart = () => {
    setStartTime(new Date());
    setElapsed(0);
    setIsRunning(true);
    showToast('Contraction started', 'info');
  };

  const handleStop = async () => {
    if (!isRunning || !startTime) return;

    const endTime = new Date();
    const durationSec = Math.round((endTime - startTime) / 1000);

    setIsRunning(false);
    setElapsed(0);

    try {
      setSavingContraction(true);
      const id = await addContraction(currentUser.email, { endTime, durationSec });
      const newEntry = { id, endTime, durationSec };
      const updatedContractions = [newEntry, ...contractions];
      setContractions(updatedContractions);
      // Refresh aggregated stats; fall back to local compute if offline
      try {
        const updatedStats = await getHourlyStats(currentUser.email);
        setHourlyStats(updatedStats);
      } catch (_) {
        setHourlyStats(computeStatsFromRecords(updatedContractions));
      }
      showToast(`Contraction recorded: ${fmtDuration(durationSec)}`, 'success');
    } catch (error) {
      console.error(error);
      showToast('Failed to save contraction', 'error');
    } finally {
      setSavingContraction(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contraction record?')) return;
    const entry = contractions.find((c) => c.id === id);
    try {
      // Pass full entry so service can decrement the correct hour bucket
      await deleteContraction(id, currentUser.email, entry.endTime, entry.durationSec);
      const updatedContractions = contractions.filter((c) => c.id !== id);
      setContractions(updatedContractions);
      // Refresh aggregated stats; fall back to local compute if offline
      try {
        const updatedStats = await getHourlyStats(currentUser.email);
        setHourlyStats(updatedStats);
      } catch (_) {
        setHourlyStats(computeStatsFromRecords(updatedContractions));
      }
      showToast('Record deleted', 'success');
    } catch (error) {
      console.error(error);
      showToast('Failed to delete record', 'error');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Clear ALL contraction records? This cannot be undone.')) return;
    try {
      await deleteAllContractions(currentUser.email);
      setContractions([]);
      setHourlyStats([]);
      showToast('All records cleared', 'success');
    } catch (error) {
      console.error(error);
      showToast('Failed to clear records', 'error');
    }
  };

  // ── stats derived from pre-aggregated state (no extra DB reads) ──────────
  const maxAvg = hourlyStats.reduce((m, s) => Math.max(m, s.avg), 1);

  // ── loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading contractions...</p>
        </div>
      </div>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-brown-50 py-8">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Header ── */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">
                🤰 Contraction Tracker
              </h1>
              <p className="text-gray-500 text-sm">
                Logged in as:{' '}
                <span className="font-semibold text-green-600">
                  {currentUser?.email}
                </span>
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-3 text-center">
              <p className="text-xs text-gray-500 mb-1">Total Recorded</p>
              <p className="text-2xl font-bold text-green-700">
                {contractions.length}
              </p>
            </div>
          </div>
        </div>

        {/* ── Timer Card ── */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 text-center">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
            {isRunning ? 'Contraction in progress…' : 'Ready to track'}
          </p>

          {/* Clock display */}
          <div
            className={`text-7xl font-mono font-bold mb-8 transition-colors duration-300 ${
              isRunning ? 'text-red-500' : 'text-gray-300'
            }`}
          >
            {fmtElapsed(elapsed)}
          </div>

          {/* Pulsing ring while running */}
          {isRunning && (
            <div className="flex justify-center mb-6">
              <span className="relative flex h-5 w-5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-5 w-5 bg-red-500"></span>
              </span>
            </div>
          )}

          <div className="flex justify-center gap-4">
            {!isRunning ? (
              <button
                onClick={handleStart}
                disabled={savingContraction}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold px-10 py-4 rounded-full text-lg shadow-md transition-all duration-200 active:scale-95"
              >
                ▶ Start
              </button>
            ) : (
              <button
                onClick={handleStop}
                disabled={savingContraction}
                className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold px-10 py-4 rounded-full text-lg shadow-md transition-all duration-200 active:scale-95"
              >
                {savingContraction ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin">⏳</span> Saving…
                  </span>
                ) : (
                  '⏹ Stop'
                )}
              </button>
            )}
          </div>

          {startTime && !isRunning && (
            <p className="text-xs text-gray-400 mt-4">
              Last started: {fmtDateTime(startTime)}
            </p>
          )}
        </div>

        {/* ── View Toggle ── */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setShowStats(false)}
            className={`flex-1 py-2 rounded-xl font-medium text-sm transition-colors duration-200 ${
              !showStats
                ? 'bg-green-600 text-white shadow'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            📋 Records
          </button>
          <button
            onClick={() => setShowStats(true)}
            className={`flex-1 py-2 rounded-xl font-medium text-sm transition-colors duration-200 ${
              showStats
                ? 'bg-green-600 text-white shadow'
                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            📊 Hourly Stats
          </button>
        </div>

        {/* ── Records Table ── */}
        {!showStats && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">
                Contraction History
              </h2>
              {contractions.length > 0 && (
                <button
                  onClick={handleClearAll}
                  className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors duration-200"
                >
                  Clear All
                </button>
              )}
            </div>

            {contractions.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <p className="text-4xl mb-3">⏱</p>
                <p className="text-sm">No contractions recorded yet.</p>
                <p className="text-xs mt-1">Press Start when a contraction begins.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        #
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        End Time (IST)
                      </th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Duration (Frequency)
                      </th>
                      <th className="text-center px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {contractions.map((c, idx) => (
                      <tr
                        key={c.id}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4 text-gray-400 font-mono text-xs">
                          {contractions.length - idx}
                        </td>
                        <td className="px-6 py-4 text-gray-700 font-medium">
                          {fmtDateTime(c.endTime)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                              c.durationSec >= 60
                                ? 'bg-red-100 text-red-700'
                                : c.durationSec >= 30
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-green-100 text-green-700'
                            }`}
                          >
                            {fmtDuration(c.durationSec)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="text-red-400 hover:text-red-600 transition-colors duration-200"
                            title="Delete"
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Stats View ── */}
        {showStats && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">
                Average Duration per Hour (IST)
              </h2>
              <p className="text-xs text-gray-400 mt-1">
                Grouped by the hour of each contraction's end time
              </p>
            </div>

            {hourlyStats.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <p className="text-4xl mb-3">📊</p>
                <p className="text-sm">No data to display yet.</p>
              </div>
            ) : (
              <>
                {/* Bar chart */}
                <div className="px-6 pt-6 pb-2">
                  <div className="space-y-3">
                    {hourlyStats.map((s) => (
                      <div key={s.hour} className="flex items-center gap-3">
                        <span className="w-32 text-right text-xs text-gray-500 shrink-0">
                          {fmtHour(s.hour)}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                          <div
                            className="h-5 rounded-full bg-green-500 transition-all duration-500 flex items-center justify-end pr-2"
                            style={{
                              width: `${Math.max(4, (s.avg / maxAvg) * 100)}%`,
                            }}
                          >
                            <span className="text-white text-xs font-semibold whitespace-nowrap">
                              {fmtDuration(s.avg)}
                            </span>
                          </div>
                        </div>
                        <span className="w-10 text-xs text-gray-400 shrink-0">
                          ×{s.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Hour Range (IST)
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Avg Duration
                        </th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {hourlyStats.map((s) => (
                        <tr
                          key={s.hour}
                          className="hover:bg-gray-50 transition-colors duration-150"
                        >
                          <td className="px-6 py-3 font-medium text-gray-700">
                            {fmtHour(s.hour)}
                          </td>
                          <td className="px-6 py-3">
                            <span className="font-semibold text-green-700">
                              {fmtDuration(s.avg)}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-gray-400">{s.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContractionTracker;
